// client/src/pages/PublicBookingFormPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchUserBySlug, submitBookingRequest } from '../api/bookingForm';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaTools, FaCheckCircle, FaArrowRight, FaArrowLeft } from 'react-icons/fa';

const PublicBookingFormPage = () => {
    const { userSlug } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        clientAddress: '',
        projectDescription: '',
        preferredDate: '',
        preferredTime: ''
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        const loadUser = async () => {
            try {
                setLoading(true);
                const response = await fetchUserBySlug(userSlug);
                if (response.success) {
                    setUser(response.user);
                } else {
                    setError('Business not found or booking is disabled.');
                }
            } catch (err) {
                console.error('Error loading user:', err);
                setError('Failed to load booking form.');
            } finally {
                setLoading(false);
            }
        };
        loadUser();
    }, [userSlug]);

    const validateStep = (currentStep) => {
        const newErrors = {};
        let isValid = true;

        if (currentStep === 1) {
            if (!formData.clientName.trim()) newErrors.clientName = 'Name is required';
            if (!formData.clientEmail.trim()) {
                newErrors.clientEmail = 'Email is required';
            } else if (!/\S+@\S+\.\S+/.test(formData.clientEmail)) {
                newErrors.clientEmail = 'Email is invalid';
            }
            if (!formData.clientPhone.trim()) newErrors.clientPhone = 'Phone is required';
        } else if (currentStep === 2) {
            if (!formData.projectDescription.trim()) newErrors.projectDescription = 'Please describe your project';
        } else if (currentStep === 3) {
            if (!formData.preferredDate) newErrors.preferredDate = 'Preferred date is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            isValid = false;
        } else {
            setErrors({});
        }
        return isValid;
    };

    const handleNext = () => {
        if (validateStep(step)) {
            setStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setStep(prev => prev - 1);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep(step)) return;

        setIsSubmitting(true);
        try {
            const response = await submitBookingRequest(userSlug, formData);
            if (response.success) {
                navigate(`/book/${userSlug}/thank-you`, { state: formData });
            } else {
                setError(response.message || 'Failed to submit booking request.');
            }
        } catch (err) {
            console.error('Submission error:', err);
            setError('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut",
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-screen bg-red-50">
                <div className="bg-white p-8 rounded-xl shadow-lg border border-red-100 max-w-md text-center">
                    <div className="text-red-500 text-5xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Unavailable</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen font-sans py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
            {/* Background decoration */}
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-blue-100 blur-3xl opacity-50"></div>
                <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-indigo-100 blur-3xl opacity-50"></div>
            </div>

            <motion.div
                className="relative z-10 w-full max-w-3xl bg-white/90 backdrop-blur-sm shadow-2xl rounded-2xl overflow-hidden border border-white/50"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* --- Hero Header --- */}
                <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8 md:p-10 text-center overflow-hidden">
                    {/* Mesh Gradient Overlay */}
                    <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-500 via-transparent to-transparent"></div>
                    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-indigo-500 via-transparent to-transparent"></div>

                    <div className="relative z-10">
                        {user?.companyLogoUrl && (
                            <img
                                src={user.companyLogoUrl}
                                alt="Company Logo"
                                className="h-20 mx-auto object-contain mb-6 drop-shadow-lg bg-white/10 backdrop-blur-md rounded-lg p-2"
                            />
                        )}
                        <h1 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight">
                            Schedule Your Appointment
                        </h1>
                        <p className="text-lg text-blue-100 max-w-xl mx-auto font-light">
                            {user?.companyName ? `with ${user.companyName}` : 'with our team'}
                        </p>
                        <p className="mt-4 text-sm text-slate-300 max-w-2xl mx-auto leading-relaxed">
                            We're excited to hear about your project! Fill out this form and let us know when works best for you. We'll confirm your appointment shortly.
                        </p>
                    </div>
                </div>

                {/* --- Progress Steps --- */}
                <div className="bg-gray-50/50 border-b border-gray-200 px-8 py-4">
                    <div className="flex items-center justify-center space-x-4 md:space-x-12">
                        <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step >= 1 ? 'border-blue-600 bg-blue-50' : 'border-gray-300'} font-bold mr-2 transition-colors`}>
                                {step > 1 ? <FaCheckCircle /> : '1'}
                            </div>
                            <span className="hidden md:inline font-medium text-sm">Contact</span>
                        </div>
                        <div className={`h-0.5 w-12 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'} transition-colors`}></div>
                        <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step >= 2 ? 'border-blue-600 bg-blue-50' : 'border-gray-300'} font-bold mr-2 transition-colors`}>
                                {step > 2 ? <FaCheckCircle /> : '2'}
                            </div>
                            <span className="hidden md:inline font-medium text-sm">Project</span>
                        </div>
                        <div className={`h-0.5 w-12 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'} transition-colors`}></div>
                        <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step >= 3 ? 'border-blue-600 bg-blue-50' : 'border-gray-300'} font-bold mr-2 transition-colors`}>
                                3
                            </div>
                            <span className="hidden md:inline font-medium text-sm">Schedule</span>
                        </div>
                    </div>
                </div>

                {/* --- Form Content --- */}
                <div className="p-8 md:p-10">
                    <form onSubmit={handleSubmit}>
                        <AnimatePresence mode="wait">
                            {step === 1 && (
                                <motion.div
                                    key="step1"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="space-y-6"
                                >
                                    <h2 className="text-2xl font-bold text-gray-800 flex items-center mb-6">
                                        <FaUser className="mr-3 text-blue-500" /> Your Details
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                            <input
                                                type="text"
                                                name="clientName"
                                                value={formData.clientName}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 rounded-lg border ${errors.clientName ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'} focus:border-blue-500 focus:ring-4 transition-all outline-none bg-gray-50 focus:bg-white`}
                                                placeholder="John Doe"
                                            />
                                            {errors.clientName && <p className="mt-1 text-sm text-red-500">{errors.clientName}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                                            <input
                                                type="email"
                                                name="clientEmail"
                                                value={formData.clientEmail}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 rounded-lg border ${errors.clientEmail ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'} focus:border-blue-500 focus:ring-4 transition-all outline-none bg-gray-50 focus:bg-white`}
                                                placeholder="john@example.com"
                                            />
                                            {errors.clientEmail && <p className="mt-1 text-sm text-red-500">{errors.clientEmail}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                                            <input
                                                type="tel"
                                                name="clientPhone"
                                                value={formData.clientPhone}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 rounded-lg border ${errors.clientPhone ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'} focus:border-blue-500 focus:ring-4 transition-all outline-none bg-gray-50 focus:bg-white`}
                                                placeholder="(555) 123-4567"
                                            />
                                            {errors.clientPhone && <p className="mt-1 text-sm text-red-500">{errors.clientPhone}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Address (Optional)</label>
                                            <input
                                                type="text"
                                                name="clientAddress"
                                                value={formData.clientAddress}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 transition-all outline-none bg-gray-50 focus:bg-white"
                                                placeholder="123 Main St"
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {step === 2 && (
                                <motion.div
                                    key="step2"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="space-y-6"
                                >
                                    <h2 className="text-2xl font-bold text-gray-800 flex items-center mb-6">
                                        <FaTools className="mr-3 text-blue-500" /> Project Details
                                    </h2>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tell us about your project *</label>
                                        <textarea
                                            name="projectDescription"
                                            value={formData.projectDescription}
                                            onChange={handleChange}
                                            rows="6"
                                            className={`w-full px-4 py-3 rounded-lg border ${errors.projectDescription ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'} focus:border-blue-500 focus:ring-4 transition-all outline-none bg-gray-50 focus:bg-white resize-none`}
                                            placeholder="I need help with..."
                                        ></textarea>
                                        {errors.projectDescription && <p className="mt-1 text-sm text-red-500">{errors.projectDescription}</p>}
                                    </div>
                                </motion.div>
                            )}

                            {step === 3 && (
                                <motion.div
                                    key="step3"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="space-y-6"
                                >
                                    <h2 className="text-2xl font-bold text-gray-800 flex items-center mb-6">
                                        <FaCalendarAlt className="mr-3 text-blue-500" /> Preferred Time
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date *</label>
                                            <input
                                                type="date"
                                                name="preferredDate"
                                                value={formData.preferredDate}
                                                onChange={handleChange}
                                                min={getMinDate()}
                                                className={`w-full px-4 py-3 rounded-lg border ${errors.preferredDate ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'} focus:border-blue-500 focus:ring-4 transition-all outline-none bg-gray-50 focus:bg-white`}
                                            />
                                            {errors.preferredDate && <p className="mt-1 text-sm text-red-500">{errors.preferredDate}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Time (Optional)</label>
                                            <select
                                                name="preferredTime"
                                                value={formData.preferredTime}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 transition-all outline-none bg-gray-50 focus:bg-white"
                                            >
                                                <option value="">Any Time</option>
                                                <option value="Morning">Morning (8am - 12pm)</option>
                                                <option value="Afternoon">Afternoon (12pm - 4pm)</option>
                                                <option value="Evening">Evening (4pm - 8pm)</option>
                                            </select>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* --- Navigation Buttons --- */}
                        <div className="mt-10 flex justify-between pt-6 border-t border-gray-100">
                            {step > 1 ? (
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="flex items-center px-6 py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors"
                                >
                                    <FaArrowLeft className="mr-2" /> Back
                                </button>
                            ) : (
                                <div></div>
                            )}

                            {step < 3 ? (
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="flex items-center px-8 py-3 bg-blue-600 text-white font-bold rounded-lg shadow-lg hover:bg-blue-700 hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                                >
                                    Next <FaArrowRight className="ml-2" />
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="flex items-center px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            Submit Request <FaCheckCircle className="ml-2" />
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </motion.div>
        </div>
    );
};

export default PublicBookingFormPage;
